import React from 'react'
import {
  useTable,
  ShowButton,
  EditButton,
  DeleteButton,
  CreateButton,
  DateField,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand, initials } from '../../theme/brand'
import { ListPage, SectionCard, AvatarDisc, EmptyHint, type Stat } from '../../theme/listkit'
import { Rocket, Newspaper, UsersRound } from '../../components/icons'

type Job = Tables<'jobs'>
type Profile = Tables<'profiles'>

// Local shape for the admin_jobs_summary view row. The generated view type has
// `id: number | null`, which doesn't satisfy Refine's BaseRecord (id must be a
// BaseKey | undefined), so we restate the aggregate columns here.
type JobsSummary = {
  total_jobs: number | null
  distinct_orgs: number | null
  distinct_posters: number | null
}

export function JobList() {
  // useTable owns the realtime channel + pagination/sort for the board grid.
  const { tableProps } = useTable<Job>({
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })

  // The board renders from the paginated table data (respects sort/page).
  const jobs = (tableProps.dataSource ?? []) as Job[]

  // Summary deck — a single bounded row from the admin summary VIEW. This
  // replaces the old unbounded useList over the whole `jobs` table (total +
  // COUNT(DISTINCT org) + COUNT(DISTINCT posted_by) all computed server-side).
  const { result: summaryRes } = useList<JobsSummary>({
    resource: 'admin_jobs_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]

  // Resolve posted_by (user_id) -> profiles.name, scoped to ONLY the posters on
  // the current page of jobs (IN filter), not the whole profiles table.
  const posterIds = React.useMemo(
    () => Array.from(new Set(jobs.map((j) => j.posted_by).filter(Boolean))) as string[],
    [jobs],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    filters: posterIds.length ? [{ field: 'user_id', operator: 'in', value: posterIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: posterIds.length > 0 },
  })
  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]))
  }, [profileRes])

  const stats: Stat[] = [
    { label: 'Total Jobs', value: (summary?.total_jobs ?? 0).toLocaleString(), icon: Rocket, tone: 'gold' },
    {
      label: 'Organizations',
      value: (summary?.distinct_orgs ?? 0).toLocaleString(),
      hint: 'hiring',
      icon: Newspaper,
      tone: 'jaguar',
    },
    {
      label: 'Posters',
      value: (summary?.distinct_posters ?? 0).toLocaleString(),
      hint: 'alumni',
      icon: UsersRound,
      tone: 'green',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Jobs',
        description:
          'The alumni job board — roles posted by members for members. Open positions, where they are, and who to thank for the lead.',
        actions: <CreateButton>Post a Job</CreateButton>,
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Open Positions" sub="Posted by alumni, newest first">
          {jobs.length === 0 ? (
            <EmptyHint>No jobs posted yet.</EmptyHint>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 14,
              }}
            >
              {jobs.map((job) => {
                const poster = profileMap[job.posted_by] ?? job.posted_by
                return (
                  <div
                    key={job.id}
                    style={{
                      border: '1px solid ' + brand.cardLine,
                      borderRadius: 14,
                      background: brand.paper,
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontFamily: brand.fDisplay,
                          fontWeight: 700,
                          fontSize: 16,
                          color: brand.ink,
                          lineHeight: 1.2,
                        }}
                      >
                        {job.role}
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 700,
                          fontSize: 12.5,
                          color: brand.jaguar,
                          marginTop: 4,
                        }}
                      >
                        {job.org}
                      </div>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12,
                          color: brand.sub,
                          marginTop: 2,
                        }}
                      >
                        {job.loc}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        paddingTop: 12,
                        borderTop: '1px solid ' + brand.cardLine,
                      }}
                    >
                      <AvatarDisc initials={initials(poster)} size={30} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 600,
                            fontSize: 12.5,
                            color: brand.ink,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {poster}
                        </div>
                        <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11, color: brand.sub }}>
                          <DateField value={job.created_at} format="MMM D, YYYY" />
                        </div>
                      </div>
                      <Space size={2}>
                        <ShowButton hideText size="small" recordItemId={job.id} />
                        <EditButton hideText size="small" recordItemId={job.id} />
                        <DeleteButton hideText size="small" recordItemId={job.id} />
                      </Space>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
      </SectionCard>
    </ListPage>
  )
}
